import { csrfMutationConfig } from './csrf';

const FIVE_MINUTES = 5 * 60 * 1000;
const WARNING_WINDOW = 2 * 60 * 1000;
const GAP_LIMIT = 60 * 1000;
const RENEWAL_LIMIT = 10000;

// Browser classification is separate so tests need not pretend JSDOM events are trusted.
export function isSessionActivity(event, visible) {
  return visible && event?.isTrusted === true
    && ['pointerdown', 'keydown', 'wheel'].includes(event.type);
}

function parseSnapshot(data) {
  if (data?.authenticated !== true) return null;
  const values = ['server_time', 'idle_expires_at', 'absolute_expires_at'].map((key) => (
    typeof data[key] === 'string' && /T.*(?:Z|[+-]\d\d:\d\d)$/.test(data[key])
      ? Date.parse(data[key]) : NaN
  ));
  const [server, idle, absolute] = values;
  if (!values.every(Number.isFinite) || idle <= server || absolute <= server) return null;
  return { server, idle, absolute };
}

// One coordinator owns confirmation, activity and deadlines. Only server responses
// establish validity; timers merely request a fresh authoritative observation.
export function installSessionLifecycle(client, onSessionLost, options = {}) {
  const now = options.now || (() => performance.now());
  const wallNow = options.wallNow || (() => Date.now());
  const page = options.document || document;
  const browser = options.window || window;
  const mutationConfig = options.mutationConfig || csrfMutationConfig;
  const onChange = options.onChange || (() => {});
  let active = true, authenticated = false, paused = false;
  let generation = 0, sequence = 0, appliedSequence = 0;
  let snapshot = null, pending = null, renewal = null;
  let cancelRenewal = () => {};
  let activity = 0, acknowledged = 0;
  let lastAttempt = now(), nextCheck = 0;
  let verified = false, errorMessage = '', lostStatus = 'AUTH_LOST';
  let lastTick = now(), lastWall = wallNow();
  const visible = () => page.visibilityState === 'visible';
  const current = (epoch) => active && generation === epoch;
  const excluded = new Set(['/api/check_session/', '/api/login/', '/api/logout/', '/api/csrf/']);
  const eligible = (config) => {
    try {
      const url = new URL(config?.url, browser.location.origin);
      return url.origin === browser.location.origin && url.pathname.startsWith('/api/') && !excluded.has(url.pathname);
    } catch { return false; }
  };
  const remaining = () => snapshot ? Math.min(snapshot.idle, snapshot.absolute) - snapshot.server
    - snapshot.latency - Math.max(0, now() - snapshot.anchor) : null;
  const absoluteRemaining = () => snapshot ? snapshot.absolute - snapshot.server
    - snapshot.latency - Math.max(0, now() - snapshot.anchor) : null;
  const state = () => {
    const timeLeft = remaining();
    const absolute = absoluteRemaining();
    let status = lostStatus;
    if (authenticated) {
      if (pending || paused || !verified) status = 'RECONCILING';
      else if (renewal) status = 'RENEWING';
      else if (timeLeft !== null && timeLeft <= WARNING_WINDOW) status = 'WARNING';
      else status = activity > acknowledged ? 'AUTHENTICATED_ACTIVE' : 'AUTHENTICATED_IDLE';
    }
    return {
      status, remainingMs: timeLeft,
      warning: authenticated && (timeLeft !== null && timeLeft <= WARNING_WINDOW),
      approachingAbsolute: absolute !== null && absolute <= WARNING_WINDOW,
      absoluteLimited: snapshot !== null && snapshot.absolute <= snapshot.idle,
      busy: Boolean(pending || renewal || paused),
      unverified: authenticated && !verified,
      // Transport eligibility; absoluteLimited separately controls useful UI extension.
      canRenew: authenticated && verified && !paused && !pending && !renewal
        && visible() && timeLeft > 0 && absolute > 0,
      error: errorMessage,
    };
  };
  const emit = () => { if (active) onChange(state()); };
  const isOperationReady = () => {
    const value = state();
    return active && authenticated && ['AUTHENTICATED_ACTIVE', 'AUTHENTICATED_IDLE', 'WARNING'].includes(value.status)
      && !value.busy && !value.unverified && value.remainingMs > 0;
  };
  const ensureOperationReady = async () => {
    const epoch = generation;
    if (!active || !authenticated || paused) return false;
    // Join existing work; publication must never initiate a renewal.
    if (renewal) await renewal;
    if (!current(epoch) || !authenticated || paused) return false;
    if (pending) await pending;
    else if (!isOperationReady()) await reconcile();
    return current(epoch) && isOperationReady();
  };
  const lose = () => {
    lostStatus = absoluteRemaining() !== null && absoluteRemaining() <= 0 ? 'ABSOLUTE_EXPIRED' : 'AUTH_LOST';
    authenticated = false;
    verified = false;
    generation += 1;
    cancelRenewal();
    acknowledged = activity;
    emit();
    onSessionLost();
  };
  const apply = (data, started, order, kind) => {
    const parsed = parseSnapshot(data);
    if (!parsed || order < appliedSequence || (snapshot && parsed.server < snapshot.server)
      || (snapshot && parsed.absolute === snapshot.absolute && parsed.idle < snapshot.idle)
      || (kind === 'renewal' && snapshot && parsed.absolute > snapshot.absolute)) return false;
    snapshot = { ...parsed, anchor: now(), latency: Math.max(0, now() - started) };
    appliedSequence = order;
    verified = true;
    errorMessage = '';
    return true;
  };

  const reconcile = () => {
    if (!active || !authenticated) return Promise.resolve(null);
    if (pending) return pending;
    const epoch = generation, order = ++sequence, started = now();
    verified = false;
    const confirmation = client.get('/api/check_session/', { withCredentials: true, timeout: 10000 })
      .then(({ data }) => {
        if (!current(epoch) || order < appliedSequence) return null;
        if (data?.authenticated === false) { lose(); return data; }
        if (!apply(data, started, order, 'check')) {
          errorMessage = 'Session validity could not be verified. Please check again.';
        } else {
          options.onSessionVerified?.(data);
        }
        return data;
      })
      .catch(() => {
        if (current(epoch) && order >= appliedSequence) errorMessage = 'Unable to verify your session. Check your connection and try again.';
        return null;
      })
      .finally(() => {
        if (pending === confirmation) pending = null;
        if (current(epoch)) { nextCheck = now() + GAP_LIMIT; emit(); }
      });
    pending = confirmation;
    emit();
    return confirmation;
  };

  const renew = (automatic = false) => {
    if (renewal) return renewal;
    if (!active || !authenticated || paused || !visible()) return Promise.resolve(false);
    if (pending || !verified || remaining() <= 0 || absoluteRemaining() <= 0) {
      return reconcile().then(() => false); // Never turn a wake/check into renewal.
    }
    if (automatic && (activity <= acknowledged || now() - lastAttempt < FIVE_MINUTES)) return Promise.resolve(false);
    const epoch = generation, order = ++sequence, started = now(), capturedActivity = activity;
    // Attempts (including failures), not successes, throttle automatic renewal.
    lastAttempt = started;
    errorMessage = '';
    let live = true, timeout;
    const controller = new AbortController();
    let release;
    const abandoned = new Promise((resolve) => { release = resolve; });
    const valid = () => live && current(epoch);
    const cancel = () => {
      live = false;
      browser.clearTimeout(timeout);
      controller.abort();
      if (renewal === operation) renewal = null;
      release(false);
    };
    const expire = () => {
      if (!valid()) return;
      cancel();
      verified = false;
      errorMessage = 'Session renewal timed out and was not verified. Check your session before trying again.';
      nextCheck = now();
      emit();
    };
    const work = Promise.resolve().then(() => valid() ? mutationConfig() : null)
      .then((config) => {
        if (!valid() || paused || !visible()) return null;
        // A suspended browser may deliver preparation before its overdue timeout.
        if (now() - started >= RENEWAL_LIMIT) { expire(); return null; }
        if (remaining() <= 0) { cancel(); reconcile(); return null; }
        return client.post('/api/session/renew/', undefined, { ...config, timeout: RENEWAL_LIMIT, signal: controller.signal });
      })
      .then((response) => {
        if (!response || !valid() || order < appliedSequence) return false;
        if (now() - started >= RENEWAL_LIMIT) { expire(); return false; }
        if (!apply(response.data, started, order, 'renewal')) {
          errorMessage = 'Renewal could not be verified. Your session deadline was not extended.';
          verified = false;
          return false;
        }
        acknowledged = Math.max(acknowledged, capturedActivity);
        return true;
      })
      .catch((error) => {
        if (!valid()) return false;
        errorMessage = 'Session renewal failed. Check your connection or sign in again; your operation was not retried.';
        if (!error.response || error.response.status >= 500) verified = false;
        // 401/403 are already confirmed by the Phase-1 response interceptor.
        return false;
      });
    const operation = Promise.race([work, abandoned]).finally(() => {
      browser.clearTimeout(timeout);
      if (!live) return;
      live = false;
      if (renewal === operation) renewal = null;
      if (current(epoch)) { nextCheck = now() + GAP_LIMIT; emit(); }
    });
    renewal = operation;
    cancelRenewal = cancel;
    timeout = browser.setTimeout(expire, RENEWAL_LIMIT);
    emit();
    return operation;
  };

  const tick = () => {
    if (!active) return;
    const time = now(), wall = wallNow();
    const gap = time - lastTick > GAP_LIMIT || Math.abs((wall - lastWall) - (time - lastTick)) > GAP_LIMIT;
    lastTick = time; lastWall = wall;
    if (!authenticated || paused || !visible()) return;
    if (gap) { acknowledged = activity; verified = false; reconcile(); }
    else if (remaining() <= 0 && (verified || time >= nextCheck)) {
      cancelRenewal();
      reconcile();
    } else if (!verified && time >= nextCheck && !renewal) reconcile();
    else if (verified && !pending && remaining() > 0) renew(true);
    emit();
  };
  const recordActivity = (event) => {
    if (active && authenticated && !paused && isSessionActivity(event, visible())) {
      activity += 1;
      emit();
    }
  };
  const wake = () => {
    acknowledged = activity;
    lastTick = now(); lastWall = wallNow();
    if (authenticated) verified = false;
    if (visible() && !paused) reconcile();
    emit();
  };
  const requestId = client.interceptors.request.use((config) => {
    config.sessionLifecycleGeneration = generation;
    return config;
  });
  const responseId = client.interceptors.response.use(undefined, async (error) => {
    if (active && authenticated && !paused && error.config?.sessionLifecycleGeneration === generation
      && eligible(error.config) && [401, 403].includes(error.response?.status)) await reconcile();
    throw error; // Never replay the failed operation.
  });
  ['pointerdown', 'keydown', 'wheel'].forEach((event) => page.addEventListener(event, recordActivity, { passive: true }));
  page.addEventListener('visibilitychange', wake);
  browser.addEventListener('focus', wake);
  browser.addEventListener('pageshow', wake);
  const timer = browser.setInterval(tick, 1000);

  return {
    reconcile, renew: () => renew(false), recordActivity, getState: state,
    isOperationReady, ensureOperationReady,
    beginTransition() {
      generation += 1;
      cancelRenewal();
      pending = null;
      paused = true;
      verified = false;
      acknowledged = activity;
      emit();
    },
    resumeAfterLogoutFailure() {
      paused = false;
      return reconcile();
    },
    setAuthenticated(value, data, started = now()) {
      generation += 1;
      cancelRenewal();
      authenticated = value === true;
      paused = false;
      pending = null;
      snapshot = null;
      appliedSequence = 0;
      verified = false;
      errorMessage = '';
      acknowledged = activity;
      lastAttempt = now(); nextCheck = now() + GAP_LIMIT;
      lastTick = now(); lastWall = wallNow();
      if (authenticated) apply(data, started, ++sequence, 'check');
      emit();
    },
    dispose() {
      active = false;
      generation += 1;
      cancelRenewal();
      browser.clearInterval(timer);
      ['pointerdown', 'keydown', 'wheel'].forEach((event) => page.removeEventListener(event, recordActivity));
      page.removeEventListener('visibilitychange', wake);
      browser.removeEventListener('focus', wake);
      browser.removeEventListener('pageshow', wake);
      client.interceptors.request.eject(requestId);
      client.interceptors.response.eject(responseId);
    },
  };
}
